import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PanelStoreState, PanelHistoryItem } from './types';

export interface PanelStoreOptions {
  /**
   * Storage key for persisting panel state (default: 'panel-storage')
   */
  storageKey?: string;
  /**
   * Default panel width in pixels (default: 500)
   */
  defaultWidth?: number;
  /**
   * Minimum panel width in pixels (default: 300)
   */
  minWidth?: number;
  /**
   * Maximum panel width as percentage of viewport (default: 0.7)
   */
  maxWidthPercent?: number;
  /**
   * Maximum number of history items to keep (default: 10)
   */
  maxHistoryItems?: number;
}

const DEFAULT_OPTIONS: Required<PanelStoreOptions> = {
  storageKey: 'panel-storage',
  defaultWidth: 500,
  minWidth: 300,
  maxWidthPercent: 0.7,
  maxHistoryItems: 10,
};

export function createPanelStore(options: PanelStoreOptions = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };

  const usePanelStore = create<PanelStoreState>()(
    persist(
      set => ({
        width: config.defaultWidth,
        isDragging: false,
        history: [],

        setWidth: (width: number) => {
          // Apply minimum width constraint
          const constrainedWidth = Math.max(config.minWidth, width);
          set({ width: constrainedWidth });
        },

        setDragging: (isDragging: boolean) => {
          set({ isDragging });
        },

        addToHistory: (type: string, id: string) => {
          set(state => {
            // Check if item already exists in history
            const existingIndex = state.history.findIndex(
              item => item.type === type && item.id === id
            );

            let newHistory: PanelHistoryItem[];

            if (existingIndex !== -1) {
              // Move existing item to the front
              newHistory = [
                { ...state.history[existingIndex], timestamp: Date.now() },
                ...state.history.filter((_, index) => index !== existingIndex),
              ];
            } else {
              // Add new item to the front
              newHistory = [
                { type, id, timestamp: Date.now() },
                ...state.history,
              ];
            }

            // Keep only the most recent items
            newHistory = newHistory.slice(0, config.maxHistoryItems);

            return { history: newHistory };
          });
        },
      }),
      {
        name: config.storageKey,
        partialize: state => ({ width: state.width }), // Only persist width
      }
    )
  );

  // Export configuration constants for consumers
  const constrainPanelWidth = (
    width: number,
    viewportWidth: number
  ): number => {
    const maxWidth = viewportWidth * config.maxWidthPercent;
    return Math.max(config.minWidth, Math.min(width, maxWidth));
  };

  return {
    usePanelStore,
    constrainPanelWidth,
    config,
  };
}

// Export default instance for backward compatibility
export const defaultPanelStore = createPanelStore();
export const usePanelStore = defaultPanelStore.usePanelStore;
export const constrainPanelWidth = defaultPanelStore.constrainPanelWidth;
export const MIN_PANEL_WIDTH = DEFAULT_OPTIONS.minWidth;
export const MAX_WIDTH_PERCENT = DEFAULT_OPTIONS.maxWidthPercent;
